import useInsightRuleSchemaMetadata from '@/Components/Hooks/useInsightRuleSchemaMetadata';
import { HSCard } from '@/Components/Common/HSWidget';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { FieldArray, Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  DashboardIconAdd,
  DashboardIconCancel,
  DashboardIconCaret,
  DashboardIconInfo,
  DashboardIconSave,
  DashboardIconStepperMinus,
  DashboardIconStepperPlus,
  DashboardIconTrash,
} from '../icons';
import {
  ActionButtons,
  AddConditionButton,
  CancelButton,
  ConditionMainRow,
  ConditionRow,
  ConditionsActionsRow,
  ConditionSecondaryRow,
  ConditionsSection,
  DayButton,
  DaysButtonsWrapper,
  DaysColumn,
  DeleteConditionButton,
  DeleteRuleButton,
  DeviceActionButton,
  DeviceActionButtonsWrapper,
  DeviceDropdown,
  DeviceMultiSelectWrapper,
  DeviceOption,
  DeviceOptionsList,
  FieldLabel,
  FormBlock,
  FormFooter,
  InfoLine,
  MultiSelectField,
  NumberField,
  NumberStepperButton,
  NumberStepperUnit,
  NumberStepperWrapper,
  RequiredText,
  RuleForm,
  RuleMainInputBlock,
  RuleTimePicker,
  RuleTopRow,
  SaveButton,
  ScheduleSection,
  SectionDivider,
  SelectField,
  SelectFieldWrapper,
  SingleOperatorPill,
  TextField,
  TimeColumn,
  TimeInputRow,
  TimezoneText,
  ToggleActions,
  ToggleCaretButton,
  ToggleItem,
  ToggleItemsWrapper,
  ToggleSwitch,
  TopSectionGrid,
  ValidationText,
} from './Rules.styles';
import {
  createCondition,
  getDisplayedConditionUnitLabel,
  initialValues,
  weekDays,
} from './RulesUtils';
dayjs.extend(isSameOrBefore);

const RuleEditStateTracker = ({ ruleId, onEditStateChange }) => {
  const { dirty } = useFormikContext();
  useEffect(
    () => onEditStateChange(ruleId, { isDirty: dirty }),
    [dirty, onEditStateChange, ruleId]
  );
  return null;
};

RuleEditStateTracker.propTypes = {
  ruleId: PropTypes.string.isRequired,
  onEditStateChange: PropTypes.func.isRequired,
};

const RuleFormCard = ({
  availableConditionTypeOptions,
  deviceDropdownState,
  defaultConditionType,
  getDefaultDeviceValuesForConditionType,
  getDeviceOptionsForConditionType,
  handleDelete,
  handleSubmit,
  is24HourFormat,
  normalizeDeviceValues,
  measurementSystem,
  rule,
  setDeviceDropdownState,
  setRules,
  timezoneSelectOptions,
  toggleRule,
  validateRuleForm,
  onEditStateChange,
  supportedCategories,
  openDialog,
  cardRef,
  scrollToRule,
}) => {
  const trans = useSelector(translator);

  // If the rule is a new unsaved draft (identified by an id that starts with 'rule-')
  const isNewRule = typeof rule.id === 'string' && rule.id.startsWith('rule-');

  const formikRef = useRef(null);
  const savedValuesRef = useRef(rule.values);
  const currentValuesRef = useRef(rule.values);
  const {
    conditionTypes = {},
    categoryOptions = [],
    conditionTypeOrder = [],
  } = useInsightRuleSchemaMetadata({ supportedCategories });

  const updateRuleValues = (valueUpdater) => {
    setRules((previousRules) =>
      previousRules.map((existingRule) => {
        if (existingRule.id !== rule.id) {
          return existingRule;
        }

        const nextValues =
          typeof valueUpdater === 'function'
            ? valueUpdater(existingRule.values)
            : { ...existingRule.values, ...valueUpdater };

        return { ...existingRule, values: nextValues };
      })
    );
  };

  const updateConditionAtIndex = (conditionIndex, conditionUpdater) => {
    updateRuleValues((previousValues) => ({
      ...previousValues,
      conditions: previousValues.conditions.map((existingCondition, existingConditionIndex) =>
        existingConditionIndex === conditionIndex
          ? typeof conditionUpdater === 'function'
            ? conditionUpdater(existingCondition)
            : { ...existingCondition, ...conditionUpdater }
          : existingCondition
      ),
    }));
  };

  const handleRuleNameChange = (event) => {
    const nextRuleName = event.target.value;
    formikRef.current?.setFieldValue('ruleName', nextRuleName);
    updateRuleValues({ ruleName: nextRuleName });
  };

  const handleEnabledChange = async (checked, event) => {
    event.stopPropagation();
    await formikRef.current?.setFieldValue('enabled', checked);
    updateRuleValues({ enabled: checked });

    // Notify depends on the rule being enabled; force it off when disabling.
    if (!checked) {
      await formikRef.current?.setFieldValue('notify', false);
      updateRuleValues({ notify: false });
    }

    if (!rule.expanded) {
      await formikRef.current?.submitForm();
    }
  };

  const handleNotifyChange = async (checked, event) => {
    event.stopPropagation();
    await formikRef.current?.setFieldValue('notify', checked);
    updateRuleValues({ notify: checked });

    if (!rule.expanded) {
      await formikRef.current?.submitForm();
    }
  };

  const handleToggleRule = (isValid, isDirty, event) => {
    if (event) event.stopPropagation();

    // Alert the user to save unsaved edits before collapsing an expanded rule.
    if (rule.expanded && (!isValid || isDirty)) {
      openDialog({
        title: trans('RULEBUILDER_ACTION_REQUIRED'),
        description: trans('RULEBUILDER_SAVE_EDITS_BEFORE_COLLAPSE'),
        confirmLabel: trans('OK'),
        confirmIcon: null,
        hideCancel: true,
        onConfirm: () => scrollToRule(rule.id),
      });
      return;
    }

    toggleRule(rule.id);
  };

  const handleCategoryChange = (event) => {
    const nextCategory = event.target.value;

    formikRef.current?.setFieldValue('category', nextCategory);
    updateRuleValues({ category: nextCategory });
  };

  const handleDeleteRule = (e) => {
    e.preventDefault();
    openDialog({
      title: trans('RULEBUILDER_DELETE_RULE'),
      userFullName: currentValuesRef.current?.ruleName,
      onConfirm: () => handleDelete(rule.id, () => formikRef.current?.resetForm()),
    });
  };

  const handleCancel = () => {
    if (isNewRule) {
      setRules((previousRules) =>
        previousRules.filter((existingRule) => existingRule.id !== rule.id)
      );
      return;
    }

    formikRef.current?.resetForm({ values: savedValuesRef.current });
    updateRuleValues(savedValuesRef.current);
    toggleRule(rule.id);
  };

  const handleStartTimeChange = (timeValue) => {
    const nextStartTime = timeValue ? timeValue.format('HH:mm') : '';

    formikRef.current?.setFieldValue('startTime', nextStartTime);
    updateRuleValues({ startTime: nextStartTime });
  };

  const handleEndTimeChange = (timeValue) => {
    const nextEndTime = timeValue ? timeValue.format('HH:mm') : '';

    formikRef.current?.setFieldValue('endTime', nextEndTime);
    updateRuleValues({ endTime: nextEndTime });
  };

  const createDayToggleHandler = (dayKey) => () => {
    const currentValues = currentValuesRef.current;
    const nextDayValue = !currentValues.days[dayKey];

    formikRef.current?.setFieldValue(`days.${dayKey}`, nextDayValue);
    updateRuleValues((previousValues) => ({
      ...previousValues,
      days: {
        ...previousValues.days,
        [dayKey]: nextDayValue,
      },
    }));
  };

  const createConditionTypeChangeHandler = (index) => (event) => {
    const nextCondition = createCondition(event.target.value, conditionTypes, conditionTypeOrder);
    nextCondition.device = getDefaultDeviceValuesForConditionType(nextCondition.type);

    formikRef.current?.setFieldValue(`conditions.${index}`, nextCondition);
    updateConditionAtIndex(index, nextCondition);
  };

  const createConditionDropdownVisibilityHandler =
    (deviceDropdownKey, selectedDeviceValues) => (nextOpen) => {
      setDeviceDropdownState((previousState) => ({
        ...previousState,
        [deviceDropdownKey]: {
          open: nextOpen,
          draftValues: selectedDeviceValues,
        },
      }));
    };

  const createConditionDeviceDraftChangeHandler =
    (deviceDropdownKey, selectedDeviceValues, deviceOptionValue) => (event) => {
      const isSelected = event.target.checked;

      setDeviceDropdownState((previousState) => {
        const previousDraft = previousState[deviceDropdownKey]?.draftValues || selectedDeviceValues;
        const nextDraft = isSelected
          ? [...previousDraft, deviceOptionValue]
          : previousDraft.filter((value) => value !== deviceOptionValue);

        return {
          ...previousState,
          [deviceDropdownKey]: {
            open: true,
            draftValues: nextDraft,
          },
        };
      });
    };

  const createConditionDeviceCancelHandler = (deviceDropdownKey, selectedDeviceValues) => () => {
    setDeviceDropdownState((previousState) => ({
      ...previousState,
      [deviceDropdownKey]: {
        open: false,
        draftValues: selectedDeviceValues,
      },
    }));
  };

  const createConditionDeviceApplyHandler =
    (index, conditionFieldPath, deviceDropdownKey, draftDeviceValues) => () => {
      formikRef.current?.setFieldValue(conditionFieldPath, draftDeviceValues);
      updateConditionAtIndex(index, { device: draftDeviceValues });
      setDeviceDropdownState((previousState) => ({
        ...previousState,
        [deviceDropdownKey]: {
          open: false,
          draftValues: draftDeviceValues,
        },
      }));
    };

  const createConditionOperatorChangeHandler = (index) => (event) => {
    const nextOperator = event.target.value;

    formikRef.current?.setFieldValue(`conditions.${index}.operator`, nextOperator);
    updateConditionAtIndex(index, { operator: nextOperator });
  };

  const createConditionValueChangeHandler = (index) => (event) => {
    const nextValue = event.target.value;

    formikRef.current?.setFieldValue(`conditions.${index}.value`, nextValue);
    updateConditionAtIndex(index, { value: nextValue });
  };

  const createConditionStepperHandler = (index, delta) => () => {
    const currentValue = Number(currentValuesRef.current.conditions[index].value || 0);
    const nextValue = currentValue + delta;

    formikRef.current?.setFieldValue(`conditions.${index}.value`, nextValue);
    updateConditionAtIndex(index, { value: nextValue });
  };

  const createAddConditionHandler = (push) => () => {
    const nextCondition = createCondition(defaultConditionType, conditionTypes, conditionTypeOrder);
    nextCondition.device = getDefaultDeviceValuesForConditionType(nextCondition.type);
    push(nextCondition);
    updateRuleValues((previousValues) => ({
      ...previousValues,
      conditions: [...previousValues.conditions, nextCondition],
    }));
  };

  const createRemoveConditionHandler = (remove, index) => () => {
    if (currentValuesRef.current.conditions.length <= 1) {
      return;
    }

    remove(index);
    updateRuleValues((previousValues) => ({
      ...previousValues,
      conditions: previousValues.conditions.filter((_, conditionIndex) => conditionIndex !== index),
    }));
  };

  return (
    <HSCard
      className="rule-card"
      style={{ display: 'flex', alignItems: 'center' }}
      content={
        <Formik
          innerRef={formikRef}
          initialValues={rule.values}
          onSubmit={(values, formikHelpers) => handleSubmit(rule.id, values, formikHelpers)}
          validate={validateRuleForm}
        >
          {({ values, errors, submitCount, isSubmitting, isValid, dirty }) => {
            currentValuesRef.current = values;
            const hasSubmitted = submitCount > 0;
            const shouldShowError = (fieldError) => Boolean(fieldError) && hasSubmitted;
            const conditionErrors = Array.isArray(errors.conditions) ? errors.conditions : [];
            const isDefaultStartEndTimes =
              values.startTime &&
              values.endTime &&
              values.startTime === initialValues.startTime &&
              values.endTime === initialValues.endTime;
            const isEndTimeBeforeStartTime =
              values.startTime &&
              values.endTime &&
              dayjs(values.endTime, 'HH:mm').isSameOrBefore(dayjs(values.startTime, 'HH:mm'));
            const isOvernightRule = !isDefaultStartEndTimes && isEndTimeBeforeStartTime;

            return (
              <RuleForm
                ref={cardRef}
                className={rule.expanded ? 'expanded' : 'collapsed'}
                onClick={() => !rule.expanded && handleToggleRule(isValid, dirty)}
              >
                <RuleEditStateTracker ruleId={rule.id} onEditStateChange={onEditStateChange} />
                <RuleTopRow>
                  <RuleMainInputBlock>
                    {rule.expanded && (
                      <FieldLabel
                        htmlFor={`ruleName-${rule.id}`}
                      >{`${trans('RULEBUILDER_RULE_LABEL')}*`}</FieldLabel>
                    )}
                    <TextField
                      type="text"
                      name="ruleName"
                      id={`ruleName-${rule.id}`}
                      className={shouldShowError(errors.ruleName) ? 'has-error' : ''}
                      placeholder={trans('RULEBUILDER_RULE_NAME_PLACEHOLDER')}
                      onChange={handleRuleNameChange}
                    />
                    {shouldShowError(errors.ruleName) && (
                      <ValidationText>{errors.ruleName}</ValidationText>
                    )}
                  </RuleMainInputBlock>

                  <ToggleActions>
                    <ToggleItemsWrapper>
                      <ToggleItem>
                        <FieldLabel htmlFor={`enabled-${rule.id}`}>
                          {trans('RULEBUILDER_ENABLED_LABEL')}
                        </FieldLabel>
                        <ToggleSwitch
                          id={`enabled-${rule.id}`}
                          checked={values.enabled}
                          onChange={handleEnabledChange}
                        />
                      </ToggleItem>
                      <ToggleItem>
                        <FieldLabel htmlFor={`notify-${rule.id}`}>
                          {trans('RULEBUILDER_NOTIFY_LABEL')}
                        </FieldLabel>
                        <ToggleSwitch
                          className={shouldShowError(errors.notify) ? 'has-error' : ''}
                          id={`notify-${rule.id}`}
                          checked={values.notify}
                          onChange={handleNotifyChange}
                          disabled={!values.enabled}
                        />
                      </ToggleItem>
                      <ToggleCaretButton
                        className={rule.expanded ? 'expanded' : ''}
                        aria-label="Expand or collapse"
                        onClick={(e) => handleToggleRule(isValid, dirty, e)}
                      >
                        <DashboardIconCaret />
                      </ToggleCaretButton>
                    </ToggleItemsWrapper>
                    {shouldShowError(errors.notify) && (
                      <ValidationText>{errors.notify}</ValidationText>
                    )}
                  </ToggleActions>
                </RuleTopRow>

                {rule.expanded && (
                  <>
                    <TopSectionGrid>
                      <FormBlock>
                        <FieldLabel htmlFor={`category-${rule.id}`}>
                          {`${trans('RULEBUILDER_CATEGORY_LABEL')}*`}
                        </FieldLabel>
                        <SelectFieldWrapper>
                          <SelectField
                            component="select"
                            name="category"
                            id={`category-${rule.id}`}
                            className={shouldShowError(errors.category) ? 'has-error' : ''}
                            onChange={handleCategoryChange}
                          >
                            <option value="">{trans('RULEBUILDER_CATEGORY_PLACEHOLDER')}</option>
                            {categoryOptions.map(({ value, label }) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ))}
                          </SelectField>
                        </SelectFieldWrapper>
                        {shouldShowError(errors.category) && (
                          <ValidationText>{errors.category}</ValidationText>
                        )}
                      </FormBlock>
                    </TopSectionGrid>

                    <FieldArray name="conditions">
                      {({ push, remove }) => (
                        <ConditionsSection>
                          <InfoLine>
                            <DashboardIconInfo />
                            {trans('RULEBUILDER_CONDITIONS_INFO')}
                          </InfoLine>
                          {typeof errors.conditions === 'string' && hasSubmitted && (
                            <ValidationText>{errors.conditions}</ValidationText>
                          )}
                          {values.conditions.map((condition, index) => {
                            const conditionConfig = conditionTypes?.[condition.type] ||
                              conditionTypes?.motion || {
                                valueType: 'select',
                                operators: [],
                                values: [],
                              };
                            const conditionError = conditionErrors[index] || {};
                            const isNumericValue = conditionConfig.valueType === 'number';
                            const conditionUnitLabel = getDisplayedConditionUnitLabel(
                              condition.type,
                              measurementSystem,
                              conditionTypes
                            );
                            const deviceOptions = getDeviceOptionsForConditionType(condition.type);
                            const conditionFieldPath = `conditions.${index}.device`;
                            const deviceDropdownKey = `${rule.id}-${index}`;
                            const selectedDeviceValues = normalizeDeviceValues(condition.device);
                            const currentDropdownState = deviceDropdownState[deviceDropdownKey] || {
                              open: false,
                              draftValues: selectedDeviceValues,
                            };
                            const draftDeviceValues = currentDropdownState.open
                              ? currentDropdownState.draftValues
                              : selectedDeviceValues;
                            const isDeleteConditionDisabled = values.conditions.length === 1;

                            return (
                              <ConditionRow key={index}>
                                <ConditionMainRow>
                                  <FormBlock>
                                    <FieldLabel htmlFor={`condition-type-${rule.id}-${index}`}>
                                      {`${trans('RULEBUILDER_CONDITION_LABEL')} ${index + 1}${index === 0 ? '*' : ''}`}
                                    </FieldLabel>
                                    <SelectFieldWrapper>
                                      <SelectField
                                        component="select"
                                        id={`condition-type-${rule.id}-${index}`}
                                        name={`conditions.${index}.type`}
                                        className={
                                          shouldShowError(conditionError.type) ? 'has-error' : ''
                                        }
                                        onChange={createConditionTypeChangeHandler(index)}
                                      >
                                        {availableConditionTypeOptions.map(({ value, label }) => (
                                          <option key={value} value={value}>
                                            {label}
                                          </option>
                                        ))}
                                      </SelectField>
                                    </SelectFieldWrapper>
                                    {shouldShowError(conditionError.type) && (
                                      <ValidationText>{conditionError.type}</ValidationText>
                                    )}
                                  </FormBlock>

                                  <FormBlock className="condition-device-block">
                                    <DeviceMultiSelectWrapper>
                                      <MultiSelectField
                                        mode="multiple"
                                        className={
                                          shouldShowError(conditionError.device) ? 'has-error' : ''
                                        }
                                        value={selectedDeviceValues}
                                        placeholder={trans('RULEBUILDER_SELECT_DEVICE')}
                                        maxTagCount={1}
                                        maxTagTextLength={100}
                                        removeIcon={null}
                                        open={Boolean(currentDropdownState.open)}
                                        onOpenChange={createConditionDropdownVisibilityHandler(
                                          deviceDropdownKey,
                                          selectedDeviceValues
                                        )}
                                        onChange={() => {}}
                                        options={deviceOptions}
                                        popupRender={() => (
                                          <DeviceDropdown
                                            role="presentation"
                                            onMouseDown={(event) => event.preventDefault()}
                                          >
                                            <DeviceOptionsList>
                                              {deviceOptions.map((deviceOption) => {
                                                const isChecked = draftDeviceValues.includes(
                                                  deviceOption.value
                                                );

                                                return (
                                                  <DeviceOption
                                                    key={deviceOption.value}
                                                    className={isChecked ? 'selected' : ''}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      checked={isChecked}
                                                      onChange={createConditionDeviceDraftChangeHandler(
                                                        deviceDropdownKey,
                                                        selectedDeviceValues,
                                                        deviceOption.value
                                                      )}
                                                    />
                                                    <span>{deviceOption.label}</span>
                                                  </DeviceOption>
                                                );
                                              })}
                                            </DeviceOptionsList>
                                            <DeviceActionButtonsWrapper>
                                              <DeviceActionButton
                                                onClick={createConditionDeviceApplyHandler(
                                                  index,
                                                  conditionFieldPath,
                                                  deviceDropdownKey,
                                                  []
                                                )}
                                              >
                                                {trans('CLEARALL')}
                                              </DeviceActionButton>
                                              <DeviceActionButton
                                                onClick={createConditionDeviceCancelHandler(
                                                  deviceDropdownKey,
                                                  selectedDeviceValues
                                                )}
                                              >
                                                {trans('CANCEL')}
                                              </DeviceActionButton>
                                              <DeviceActionButton
                                                className="apply"
                                                onClick={createConditionDeviceApplyHandler(
                                                  index,
                                                  conditionFieldPath,
                                                  deviceDropdownKey,
                                                  draftDeviceValues
                                                )}
                                              >
                                                {trans('APPLY')}
                                              </DeviceActionButton>
                                            </DeviceActionButtonsWrapper>
                                          </DeviceDropdown>
                                        )}
                                      />
                                    </DeviceMultiSelectWrapper>
                                    {shouldShowError(conditionError.device) && (
                                      <ValidationText>{conditionError.device}</ValidationText>
                                    )}
                                  </FormBlock>

                                  <DeleteConditionButton
                                    onClick={createRemoveConditionHandler(remove, index)}
                                    disabled={isDeleteConditionDisabled}
                                    aria-label="Delete condition"
                                  >
                                    <DashboardIconTrash />
                                  </DeleteConditionButton>
                                </ConditionMainRow>

                                <ConditionSecondaryRow>
                                  <FormBlock className="compact-field">
                                    {conditionConfig.operators.length === 1 ? (
                                      <SingleOperatorPill>
                                        {conditionConfig.operators[0].label}
                                      </SingleOperatorPill>
                                    ) : (
                                      <SelectFieldWrapper>
                                        <SelectField
                                          component="select"
                                          name={`conditions.${index}.operator`}
                                          className={
                                            shouldShowError(conditionError.operator)
                                              ? 'has-error'
                                              : ''
                                          }
                                          onChange={createConditionOperatorChangeHandler(index)}
                                        >
                                          {conditionConfig.operators.map(({ value, label }) => (
                                            <option key={value} value={value}>
                                              {label}
                                            </option>
                                          ))}
                                        </SelectField>
                                      </SelectFieldWrapper>
                                    )}
                                    {shouldShowError(conditionError.operator) && (
                                      <ValidationText>{conditionError.operator}</ValidationText>
                                    )}
                                  </FormBlock>

                                  <FormBlock className="compact-field">
                                    {isNumericValue ? (
                                      <NumberStepperWrapper
                                        className={
                                          shouldShowError(conditionError.value) ? 'has-error' : ''
                                        }
                                      >
                                        <NumberStepperButton
                                          onClick={createConditionStepperHandler(index, -1)}
                                        >
                                          <DashboardIconStepperMinus />
                                        </NumberStepperButton>
                                        <NumberField
                                          type="number"
                                          name={`conditions.${index}.value`}
                                          onChange={createConditionValueChangeHandler(index)}
                                        />
                                        <NumberStepperButton
                                          onClick={createConditionStepperHandler(index, 1)}
                                        >
                                          <DashboardIconStepperPlus />
                                        </NumberStepperButton>
                                        {conditionUnitLabel && (
                                          <NumberStepperUnit>
                                            {conditionUnitLabel}
                                          </NumberStepperUnit>
                                        )}
                                      </NumberStepperWrapper>
                                    ) : (conditionConfig?.values || []).length === 1 ? (
                                      <SingleOperatorPill>
                                        {conditionConfig.values[0].label}
                                      </SingleOperatorPill>
                                    ) : (
                                      <SelectFieldWrapper>
                                        <SelectField
                                          component="select"
                                          name={`conditions.${index}.value`}
                                          className={
                                            shouldShowError(conditionError.value) ? 'has-error' : ''
                                          }
                                          onChange={createConditionValueChangeHandler(index)}
                                        >
                                          {conditionConfig.values.map(({ value, label }) => (
                                            <option key={value} value={value}>
                                              {label}
                                            </option>
                                          ))}
                                        </SelectField>
                                      </SelectFieldWrapper>
                                    )}
                                    {shouldShowError(conditionError.value) && (
                                      <ValidationText>{conditionError.value}</ValidationText>
                                    )}
                                  </FormBlock>
                                </ConditionSecondaryRow>
                              </ConditionRow>
                            );
                          })}

                          <ConditionsActionsRow>
                            <AddConditionButton onClick={createAddConditionHandler(push)}>
                              <DashboardIconAdd /> {trans('RULEBUILDER_ADD_CONDITION')}
                            </AddConditionButton>
                          </ConditionsActionsRow>
                        </ConditionsSection>
                      )}
                    </FieldArray>

                    <SectionDivider />

                    <ScheduleSection>
                      <DaysColumn>
                        <FieldLabel
                          htmlFor={`days-${rule.id}`}
                        >{`${trans('RULEBUILDER_DAYS')}*`}</FieldLabel>
                        <DaysButtonsWrapper id={`days-${rule.id}`}>
                          {weekDays.map((day) => (
                            <DayButton
                              key={day.key}
                              className={`${values.days[day.key] ? 'active' : ''} ${shouldShowError(errors.days) ? 'has-error' : ''}`}
                              onClick={createDayToggleHandler(day.key)}
                            >
                              {day.label}
                            </DayButton>
                          ))}
                        </DaysButtonsWrapper>
                        {shouldShowError(errors.days) && (
                          <ValidationText>{errors.days}</ValidationText>
                        )}
                        {weekDays.map((day) => values.days[day.key]).every(Boolean) && (
                          <InfoLine>
                            <DashboardIconInfo className="info-line-icon" />
                            {trans('RULEBUILDER_DAYS_INFO')}
                          </InfoLine>
                        )}
                      </DaysColumn>

                      <TimeColumn>
                        <FieldLabel
                          htmlFor={`startTime-${rule.id}`}
                        >{`${trans('RULEBUILDER_START')}*`}</FieldLabel>
                        <TimeInputRow>
                          <RuleTimePicker
                            id={`startTime-${rule.id}`}
                            className={shouldShowError(errors.startTime) ? 'has-error' : ''}
                            placeholder={trans('RULEBUILDER_SELECT_TIME')}
                            value={values.startTime ? dayjs(values.startTime, 'HH:mm') : null}
                            format={is24HourFormat ? 'HH:mm' : 'h:mm a'}
                            minuteStep={15}
                            allowClear={false}
                            size="large"
                            onChange={handleStartTimeChange}
                            suffixIcon={
                              <TimezoneText>
                                {values.startTimezone || timezoneSelectOptions[0]?.label || 'UTC'}
                              </TimezoneText>
                            }
                          />
                          {shouldShowError(errors.startTime) && (
                            <ValidationText>{errors.startTime}</ValidationText>
                          )}
                        </TimeInputRow>
                      </TimeColumn>

                      <TimeColumn>
                        <FieldLabel
                          htmlFor={`endTime-${rule.id}`}
                        >{`${trans('RULEBUILDER_END')}*`}</FieldLabel>
                        <TimeInputRow>
                          <RuleTimePicker
                            id={`endTime-${rule.id}`}
                            className={shouldShowError(errors.endTime) ? 'has-error' : ''}
                            placeholder={trans('RULEBUILDER_SELECT_TIME')}
                            value={values.endTime ? dayjs(values.endTime, 'HH:mm') : null}
                            format={is24HourFormat ? 'HH:mm' : 'h:mm a'}
                            minuteStep={15}
                            allowClear={false}
                            size="large"
                            onChange={handleEndTimeChange}
                            suffixIcon={
                              <TimezoneText>
                                {values.endTimezone || timezoneSelectOptions[0]?.label || 'UTC'}
                              </TimezoneText>
                            }
                          />
                          {shouldShowError(errors.endTime) && (
                            <ValidationText>{errors.endTime}</ValidationText>
                          )}
                          {isOvernightRule && (
                            <InfoLine>
                              <DashboardIconInfo className="info-line-icon" />
                              {trans('RULEBUILDER_END_TIME_INFO')}
                            </InfoLine>
                          )}
                        </TimeInputRow>
                      </TimeColumn>
                    </ScheduleSection>

                    <SectionDivider />

                    <FormFooter>
                      <RequiredText>{`* ${trans('RULEBUILDER_REQUIRED_FIELDS')}`}</RequiredText>
                      <ActionButtons>
                        {!isNewRule && (
                          <DeleteRuleButton onClick={handleDeleteRule}>
                            <DashboardIconTrash /> {trans('RULEBUILDER_DELETE_RULE')}
                          </DeleteRuleButton>
                        )}
                        <CancelButton onClick={handleCancel}>
                          <DashboardIconCancel /> {trans('CANCEL')}
                        </CancelButton>
                        <SaveButton disabled={isSubmitting}>
                          <DashboardIconSave /> {trans('SAVE')}
                        </SaveButton>
                      </ActionButtons>
                    </FormFooter>
                  </>
                )}
              </RuleForm>
            );
          }}
        </Formik>
      }
    />
  );
};

RuleFormCard.propTypes = {
  availableConditionTypeOptions: PropTypes.array,
  deviceDropdownState: PropTypes.object,
  defaultConditionType: PropTypes.string,
  getDefaultDeviceValuesForConditionType: PropTypes.func,
  getDeviceOptionsForConditionType: PropTypes.func,
  handleDelete: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  is24HourFormat: PropTypes.bool,
  measurementSystem: PropTypes.string,
  normalizeDeviceValues: PropTypes.func,
  rule: PropTypes.object,
  setDeviceDropdownState: PropTypes.func,
  setRules: PropTypes.func,
  timezoneSelectOptions: PropTypes.array,
  toggleRule: PropTypes.func,
  validateRuleForm: PropTypes.func,
  onEditStateChange: PropTypes.func,
  supportedCategories: PropTypes.array,
  openDialog: PropTypes.func,
  cardRef: PropTypes.func,
  scrollToRule: PropTypes.func,
};

export default RuleFormCard;
